"use client"
import { useChains } from "@opensea/app-kit"
import { EmptyState, Spinner, useTheme, useToasts } from "@opensea/ui-kit"
import { Suspense, useEffect, useMemo, useState } from "react"
import { useClient } from "urql"
import { useTranslations } from "use-intl"
import { SmartContractWalletWarningModal } from "../../../components/SmartContractWalletWarningModal"
import {
  MoonPayBuyWidget,
  MoonPayProvider,
} from "../../../lib/moonpay/components"
import {
  MOONPAY_CLIENT_API_KEY,
  MOONPAY_THEME_ID,
} from "../../../lib/moonpay/constants"
import { fetchMoonPayUrlSignature } from "../../../lib/moonpay/fetchMoonPayUrlSignature"
import { useIsMoonPayAllowed } from "../../../lib/moonpay/useIsMoonPayAllowed"
import { getDefaultMoonPayCurrencyCode } from "../../../lib/moonpay/utils"
import { useAddress } from "../../../polychain/hooks"
import { useChainArch } from "../../../polychain/hooks/useChainArch"
import { useChainId } from "../../../polychain/hooks/useChainId"
import { useIsSmartContractWallet } from "../../hooks/useIsSmartContractWallet"

export const MoonPayTopupWidget = () => {
  const { isTopupAllowed, isLoading } = useIsMoonPayAllowed()
  const { theme } = useTheme()
  const [hasError, setHasError] = useState(false)
  const address = useAddress()
  const client = useClient()
  const t = useTranslations("wallet.MoonPayTopupWidget")
  const { showSuccessMessage } = useToasts()

  // Get the currently connected chain
  const { chains } = useChains()
  const activeChainId = useChainId()
  const activeChainArch = useChainArch()
  const chain = useMemo(() => {
    return chains.find(
      c => c.networkId === activeChainId && c.arch === activeChainArch,
    )
  }, [chains, activeChainId, activeChainArch])

  const isSmartContractWallet = useIsSmartContractWallet()
  const [
    smartContractWalletWarningModalOpen,
    setSmartContractWalletWarningModalOpen,
  ] = useState(false)
  const [userHasAcknowledgedWarning, setUserHasAcknowledgedWarning] =
    useState(false)

  useEffect(() => {
    if (isSmartContractWallet && !userHasAcknowledgedWarning) {
      setSmartContractWalletWarningModalOpen(true)
    }
  }, [isSmartContractWallet, userHasAcknowledgedWarning])

  if (!(isTopupAllowed || isLoading)) {
    return <EmptyState title={t("notSupported")} variant="no-data" />
  }

  if (hasError) {
    return <EmptyState variant="no-data" />
  }

  return (
    <>
      <Suspense fallback={<Spinner size="lg" />}>
        <MoonPayProvider apiKey={MOONPAY_CLIENT_API_KEY}>
          <MoonPayBuyWidget
            defaultCurrencyCode={
              chain?.identifier
                ? getDefaultMoonPayCurrencyCode(chain.identifier)
                : undefined
            }
            onTransactionCompleted={async () => {
              showSuccessMessage(t("success"))
            }}
            onUrlSignatureRequested={async url => {
              const result = await fetchMoonPayUrlSignature(url, client)
              if (!result) {
                setHasError(true)
              }
              return result
            }}
            showAllCurrencies="true"
            style={{
              height: "100%",
              margin: 0,
              width: "100%",
              border: "1px solid rgb(var(--color-border-1))",
            }}
            theme={theme}
            themeId={MOONPAY_THEME_ID}
            variant="embedded"
            walletAddress={address}
          />
        </MoonPayProvider>
      </Suspense>
      <SmartContractWalletWarningModal
        onCancel={() => {
          setSmartContractWalletWarningModalOpen(false)
        }}
        onContinue={() => {
          setSmartContractWalletWarningModalOpen(false)
          setUserHasAcknowledgedWarning(true)
        }}
        open={smartContractWalletWarningModalOpen}
      />
    </>
  )
}
